/**
 * Phase F — Pre-breakfast category multi-select panel.
 *
 * Mounts when `selectedCodes.length > 0` AND `breakfast_started === false`.
 * Tristate checkboxes:
 *   - checked    iff EVERY selected table has the category
 *   - unchecked  iff NO selected table has the category
 *   - indeterminate otherwise
 *
 * Toggling pushes a single bulk API call: POST /api/tables/categories/bulk
 */
import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import useAppStore from "@/store/useAppStore";
import useSelectionStore from "@/store/useSelectionStore";
import { useBulkUpdateCategories } from "@/hooks/useTableMutations";
import { CATEGORY_COLORS, CATEGORY_PRIORITY } from "@/utils/categories";

const CATEGORY_LABELS = {
  vip: "VIP",
  email: "Email",
  complain: "Complain",
};

function computeTriState(selectedTables, category) {
  if (selectedTables.length === 0) return false;
  let count = 0;
  for (const t of selectedTables) {
    if ((t.categories || []).includes(category)) count += 1;
  }
  if (count === 0) return false;
  if (count === selectedTables.length) return true;
  return "indeterminate";
}

export default function CategoryMultiSelectPanel() {
  const tables = useAppStore((s) => s.tables);
  const selectedCodes = useSelectionStore((s) => s.selectedCodes);
  const clear = useSelectionStore((s) => s.clear);
  const bulk = useBulkUpdateCategories();

  const selectedTables = useMemo(
    () => selectedCodes.map((c) => tables[c]).filter(Boolean),
    [selectedCodes, tables],
  );

  if (selectedTables.length === 0) return null;

  const handleToggle = (category, currentState) => {
    // Tristate cycle:
    //   unchecked (false) → add to all
    //   indeterminate     → add to all (fill)
    //   checked (true)    → remove from all
    const add = currentState !== true;
    bulk.mutate({ codes: selectedCodes, category, add });
  };

  return (
    <div
      data-testid="category-panel"
      className="px-4 py-3 border-b"
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#E5E7EB",
      }}
    >
      <div className="flex items-center justify-center sm:justify-between gap-4">
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-xs font-semibold" style={{ color: "#424242" }}>
            {selectedTables.length} selected
          </span>
          <button
            data-testid="category-panel-clear"
            onClick={clear}
            className="text-xs underline"
            style={{ color: "#2196F3" }}
          >
            Clear
          </button>
        </div>

        <div className="flex items-center gap-5">
          {CATEGORY_PRIORITY.map((cat) => {
            const tri = computeTriState(selectedTables, cat);
            const checked = tri === true;
            const indeterminate = tri === "indeterminate";
            return (
              <label
                key={cat}
                data-testid={`category-checkbox-${cat}`}
                className="flex items-center gap-2 cursor-pointer select-none"
              >
                <Checkbox
                  checked={indeterminate ? "indeterminate" : checked}
                  onCheckedChange={() => handleToggle(cat, tri)}
                  disabled={bulk.isPending}
                />
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-block rounded-full"
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: CATEGORY_COLORS[cat].dot,
                    }}
                  />
                  <span className="text-sm" style={{ color: "#424242" }}>
                    {CATEGORY_LABELS[cat]}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
